import React, { memo, useState } from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  Checkbox,
  CountrySelect,
  HiddenField,
} from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { safeGetMessage } from '@xbcb/ui-utils';
import { Form, Row } from 'antd';
import DecimalQuantity from 'components/DecimalQuantity';
import { useTheme } from 'styled-components';
import { WeightUnit } from '__generated__/graphql';

export interface SteelMeltAndPourCountryDetailProps {
  localNamePath: NamePath;
  fullNamePath: NamePath;
}

export const steelCountryOfMeltAndPourLabel = 'Country of Melt and Pour';
export const derivativeSteelProductOnlyLabel = 'Only Derivative Steel Articles';

export enum Section232SteelRequirementsFields {
  STEEL_COUNTRY_OF_MELT_AND_POUR = 'steelCountryOfMeltAndPour',
  // Regulation was changed from Mexico only to all countries.
  DERIVATIVE_STEEL_PRODUCT_ONLY = 'derivativeSteelProductOfMexicoOnly',
}

export const steelRequirementsFieldPrefix = 'steelRequirements';

const doesNotContainSteelField = 'doesNotContainSteel';
export const doesNotContainSteelLabel = 'Does not contain steel';

const netSteelWeightFieldPrefix = 'netSteelWeight';
export const totalSteelNetWeightLabel = 'Total Steel Net Weight';

export const SteelMeltAndPourCountryDetail: React.FC<
  SteelMeltAndPourCountryDetailProps
> = ({ localNamePath, fullNamePath }) => {
  const theme = useTheme();
  const [bundle] = useBundle('components.SteelMeltAndPourCountryDetail');
  const fieldLocalNamePath = (field: Section232SteelRequirementsFields) => {
    return [...localNamePath, steelRequirementsFieldPrefix, field];
  };

  const fieldFullNamePath = (fieldName: Section232SteelRequirementsFields) => {
    return [...fullNamePath, steelRequirementsFieldPrefix, fieldName];
  };

  const form = Form.useFormInstance();

  const [doesNotContainSteel, setDoesNotContainSteel] = useState(() =>
    Boolean(form.getFieldValue([...fullNamePath, doesNotContainSteelField])),
  );

  const derivativeSteelProductOnly = Form.useWatch(
    fieldFullNamePath(
      Section232SteelRequirementsFields.DERIVATIVE_STEEL_PRODUCT_ONLY,
    ),
  );

  return (
    <>
      <Row>
        {!doesNotContainSteel && (
          <Checkbox
            localNamePath={fieldLocalNamePath(
              Section232SteelRequirementsFields.DERIVATIVE_STEEL_PRODUCT_ONLY,
            )}
            fullNamePath={fieldFullNamePath(
              Section232SteelRequirementsFields.DERIVATIVE_STEEL_PRODUCT_ONLY,
            )}
            form={form}
            text={derivativeSteelProductOnlyLabel}
          />
        )}
        <Checkbox
          localNamePath={[...localNamePath, doesNotContainSteelField]}
          fullNamePath={[...fullNamePath, doesNotContainSteelField]}
          form={form}
          text={doesNotContainSteelLabel}
          onChange={(event) => {
            setDoesNotContainSteel(event.target.checked);
            form.setFields([
              {
                name: [...fullNamePath, doesNotContainSteelField],
                value: event.target.checked,
              },
              {
                name: [...fullNamePath, steelRequirementsFieldPrefix],
                value: undefined,
              },
            ]);
          }}
        />
      </Row>
      {!doesNotContainSteel && (
        <>
          <CountrySelect
            label={steelCountryOfMeltAndPourLabel}
            localNamePath={fieldLocalNamePath(
              Section232SteelRequirementsFields.STEEL_COUNTRY_OF_MELT_AND_POUR,
            )}
            required={!derivativeSteelProductOnly}
          />
          <DecimalQuantity
            label={safeGetMessage(
              bundle,
              'total_steel_net_weight',
              undefined,
              totalSteelNetWeightLabel,
            )}
            field={[
              ...localNamePath,
              steelRequirementsFieldPrefix,
              netSteelWeightFieldPrefix,
              'value',
            ]}
            units="kg"
            $itemSize={theme.size.tiny_short}
          />
          <HiddenField
            localNamePath={[
              ...localNamePath,
              steelRequirementsFieldPrefix,
              netSteelWeightFieldPrefix,
              'unit',
            ]}
            fullNamePath={[
              ...fullNamePath,
              steelRequirementsFieldPrefix,
              netSteelWeightFieldPrefix,
              'unit',
            ]}
            value={WeightUnit.KILOGRAM}
            form={form}
          />
        </>
      )}
    </>
  );
};

export default memo(SteelMeltAndPourCountryDetail);
